import type { TableEditResult } from '../schemas'
import { SingleSelect, MultiSelect } from '@/assets/icons'
import Checkbox from '@/components/checkbox/Checkbox'
import { Select } from '@/components/select/Select'
import { columnIcons, getColumnIcon } from './column-icons'
import { useEffect, useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import FloatLabelFormTextArea from '@/matter/components/matter-dialogs/FloatLabelFormTextArea'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { ColumnKind, type TableColumn, type TableEdit } from '../schemas'
import ColumnTagsInput, { type DraftTag } from './ColumnTagsInput'

enum SelectionFormat {
  Single = 'single-select',
  Multiple = 'multi-select',
}

export default function ColumnForm({
  column,
  documentBased,
  saving,
  loading = false,
  onSave,
  onClose,
  onDirtyChange,
  error,
}: {
  error?: string | null
  column?: TableColumn
  documentBased: boolean
  saving: boolean
  loading?: boolean
  onSave: (edit: TableEdit) => Promise<TableEditResult>
  onClose: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const formId = useId()
  const { register, watch } = useForm({ defaultValues: { columnName: column?.name ?? '', question: column?.question ?? '' } })
  const { columnName, question } = watch()
  const [manualEntry, setManualEntry] = useState(column?.manual_entry ?? false)
  const [kind, setKind] = useState<ColumnKind>(column?.kind ?? ColumnKind.Text)
  const [multipleDraft, setAllowMultiple] = useState(column?.allow_multiple ?? false)
  const isAnalysisColumn = documentBased && !manualEntry
  const multipleLocked = Boolean(column?.allow_multiple && !isAnalysisColumn)
  const allowMultiple = multipleLocked || multipleDraft
  const [tags, setTags] = useState<DraftTag[]>(() => (column?.tags ?? []).map((tag, id) => ({ ...tag, id })))
  const [expectedTags] = useState(() => column?.tags ?? [])
  const savedTags = tags.map(({ label, color, custom_color }) => ({ label: label.trim(), color, custom_color }))
  const tagsChanged = JSON.stringify(savedTags) !== JSON.stringify(expectedTags)
  const tagError =
    kind !== ColumnKind.Tags
      ? null
      : !tags.length
      ? 'Add at least one tag.'
      : savedTags.some((tag) => !tag.label)
      ? 'Tag names cannot be blank.'
      : new Set(savedTags.map((tag) => tag.label)).size !== tags.length
      ? 'Tag names must be unique.'
      : null
  const choicesChanged =
    column &&
    ((kind !== column.kind && (kind === ColumnKind.Tags || column.kind === ColumnKind.Tags)) ||
      (kind === ColumnKind.Tags && allowMultiple !== column.allow_multiple) ||
      JSON.stringify(savedTags.map((tag) => tag.label).sort()) !== JSON.stringify(expectedTags.map((tag) => tag.label).sort()))
  const dirty =
    columnName !== (column?.name ?? '') ||
    question !== (column?.question ?? '') ||
    manualEntry !== (column?.manual_entry ?? false) ||
    kind !== (column?.kind ?? ColumnKind.Text) ||
    tagsChanged ||
    (kind === ColumnKind.Tags && allowMultiple !== (column?.allow_multiple ?? false))
  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  const labelRequired = Boolean(column) || !isAnalysisColumn
  const format = kind === ColumnKind.Tags ? (allowMultiple ? SelectionFormat.Multiple : SelectionFormat.Single) : kind
  const FormatIcon = getColumnIcon(kind, allowMultiple)
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (event) => {
        event.preventDefault()
        if (tagError) return
        const tagFields =
          kind === ColumnKind.Tags
            ? { tags: savedTags, allow_multiple: allowMultiple }
            : column?.kind === ColumnKind.Tags
            ? { tags: [], allow_multiple: false }
            : {}
        const name = columnName.trim()
        let edit: TableEdit
        if (column) {
          edit = {
            action: 'edit-column',
            column_id: column.id,
            name,
            question: question.trim(),
            ...(documentBased ? { manual_entry: manualEntry } : {}),
            ...(kind !== column.kind ? { kind } : {}),
            ...('tags' in tagFields ? { expected_tags: expectedTags } : {}),
          }
        } else if (isAnalysisColumn && !name) {
          edit = { action: 'add-analysis-column', question: question.trim(), kind }
        } else if (isAnalysisColumn) {
          edit = { action: 'add-column', name, question: question.trim(), kind }
        } else {
          edit = { action: 'add-column', name, kind, ...(documentBased ? { manual_entry: true } : {}) }
        }
        const result = await onSave({ ...edit, ...tagFields })
        if (result.ok || result.conflict) onClose()
      }}
    >
      <label className="flex items-center gap-3 rounded border border-brand-neutral-400 px-3 py-2 focus-within:ring-1 focus-within:ring-brand-500">
        <FormatIcon className="h-5 w-5 shrink-0 text-brand-neutral-950" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="text-xs text-brand-neutral-500">Format *</span>
          <Select
            ariaLabel="Format"
            required
            selectedItemId={format}
            onSelect={(value) => {
              if (value === SelectionFormat.Single || value === SelectionFormat.Multiple) {
                setKind(ColumnKind.Tags)
                setAllowMultiple(value === SelectionFormat.Multiple)
              } else {
                setKind(value as ColumnKind)
              }
            }}
            disabled={saving}
            triggerClassName="h-auto border-0 bg-transparent p-0 text-brand-neutral-950 focus-visible:ring-0"
            items={[
              { id: ColumnKind.Text, label: 'Text', leadingIcon: <columnIcons.text aria-hidden /> },
              { id: ColumnKind.Number, label: 'Number', leadingIcon: <columnIcons.number aria-hidden /> },
              { id: ColumnKind.Date, label: 'Date', leadingIcon: <columnIcons.date aria-hidden /> },
              { id: SelectionFormat.Single, label: 'Single Select', leadingIcon: <SingleSelect aria-hidden />, disabled: multipleLocked },
              { id: SelectionFormat.Multiple, label: 'Multi-select', leadingIcon: <MultiSelect aria-hidden /> },
            ]}
          />
        </span>
      </label>
      {column && kind !== column.kind && !choicesChanged && question.trim() === column.question && (
        <p className="text-xs text-brand-neutral-600">
          Changing only format preserves answers and citations without rerunning analysis. Filters that depend on the previous format will be removed.
        </p>
      )}
      {documentBased && (
        <label className="flex items-center gap-2 text-sm text-brand-neutral-800">
          <Checkbox checked={manualEntry} onCheckedChange={(checked) => setManualEntry(checked === true)} disabled={saving} />
          Manual entry
        </label>
      )}
      {isAnalysisColumn && (
        <FloatLabelFormTextArea
          id={`${formId}-question`}
          label="Analysis question"
          required={!column}
          registration={register('question')}
          rows={kind === ColumnKind.Tags ? 1 : undefined}
          disabled={saving}
          textareaProps={{ 'aria-label': 'Analysis question', required: !column }}
        />
      )}
      <InputFieldFloatingLabel
        id={`${formId}-label`}
        label="Column label"
        required={labelRequired}
        registration={register('columnName')}
        disabled={saving}
        inputProps={{ 'aria-label': 'Column label', required: labelRequired }}
      />
      {documentBased && manualEntry && <p className="text-xs text-brand-neutral-600">Enter values yourself. Document analysis will skip this column.</p>}
      <div hidden={kind !== ColumnKind.Tags}>
        {multipleLocked && (
          <p className="mb-3 text-xs text-brand-neutral-600">Saved multi-select columns cannot be changed to single select. Create a new column instead.</p>
        )}
        <ColumnTagsInput analysis={isAnalysisColumn} disabled={saving} tags={tags} onChange={setTags} />
      </div>
      {tagError && (
        <p role="alert" className="text-xs text-red-700">
          {tagError}
        </p>
      )}
      {choicesChanged && isAnalysisColumn && (
        <p className="text-xs text-brand-neutral-600">Changing tag choices or selection mode will hide existing answers until you rerun analysis.</p>
      )}
      {column &&
        !isAnalysisColumn &&
        kind === ColumnKind.Tags &&
        (column.kind !== ColumnKind.Tags ? (
          <InlineMessage variant="warning">Cell values that do not exactly match a tag will be cleared when you save.</InlineMessage>
        ) : expectedTags.some((tag) => !savedTags.some((saved) => saved.label === tag.label)) ? (
          <InlineMessage variant="warning">Renaming or removing tags will remove those tags from existing cells.</InlineMessage>
        ) : null)}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-brand-neutral-500">
          <span className="text-red-700">*</span> Required fields
        </span>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outlined" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            disabled={Boolean(tagError) || (labelRequired && !columnName.trim()) || (isAnalysisColumn && !column && !question.trim()) || saving || loading}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </form>
  )
}
